import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Optional heading shown in the fallback UI */
  heading?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Catches unhandled render errors in a subtree and shows a recovery UI
 * instead of crashing the entire page.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary]", error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
  }

  handleReset = () => {
    this.setState({ hasError: false, message: "" });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 rounded-lg border border-destructive/30 bg-destructive/5 p-8 text-center">
        <AlertTriangle className="h-10 w-10 text-destructive" />
        <div>
          <p className="font-semibold text-destructive">
            {this.props.heading ?? "Something went wrong"}
          </p>
          {this.state.message && (
            <p className="mt-1 text-sm text-muted-foreground">{this.state.message}</p>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={this.handleReset}>
          Try again
        </Button>
      </div>
    );
  }
}
